import './lib/polyfills'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { Capacitor } from '@capacitor/core'
import { StatusBar } from '@capacitor/status-bar'
import { log } from './lib/logger'
import { registerSW } from 'virtual:pwa-register'
import { AuthProvider } from './contexts/AuthProvider'
import { BlockedUsersProvider } from './contexts/BlockedUsersContext'
import { FavoriteFriendsProvider } from './contexts/FavoriteFriendsContext'
import { ClockProvider } from './contexts/ClockProvider'
import { TimeFormatProvider } from './contexts/TimeFormatProvider'
import { initSentry, Sentry } from './lib/sentry'
import { titlePrefix } from './lib/worktreeLabel'
import { installDebug } from './lib/debug'
import { installFirestoreRecovery } from './lib/firestoreRecovery'
import { installSessionDiagnostics } from './lib/sessionDiagnostics'
import { installStaleChunkReload } from './lib/staleChunkReload'
import { ErrorFallback } from './components/ErrorFallback'
import { Toaster } from './components/ui/sonner'
import { LocaleContext } from './lib/locale-context'
import { applyReadyBundleAtLaunch } from './lib/ota'
import { FeedbackDialog } from './components/FeedbackDialog'
import App from './App.tsx'
import './index.css'

initSentry()
installDebug()
// Lets CSS tune chrome per shell — e.g. the bottom nav dips into iOS's home-indicator
// inset, which on Android would be the (possibly 3-button) system nav bar.
document.documentElement.dataset.platform = Capacitor.getPlatform()
installSessionDiagnostics()
installFirestoreRecovery()

// index.html ships a static <title>, so stamp the worktree label at boot too —
// otherwise the tab reads plain "Beacon" until the first page's useDocumentTitle.
if (titlePrefix) {
  document.title = titlePrefix + document.title
}

// iOS zooms the WebView into any focused input under 16px and leaves it
// zoomed; a native app shouldn't zoom at all. Web keeps pinch-zoom for a11y.
if (Capacitor.isNativePlatform()) {
  document
    .querySelector('meta[name="viewport"]')
    ?.setAttribute(
      'content',
      'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover',
    )
}

// iOS: draw the WebView under the status bar / Dynamic Island so the floating header's
// pills (and the page behind them) reach the top edge instead of a solid native band.
// AppHeader already pads by env(safe-area-inset-top). Set at runtime, not in
// capacitor.config, so it ships over OTA to existing shells; Android keeps its bar.
if (Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('StatusBar')) {
  StatusBar.setOverlaysWebView({ overlay: true }).catch(err => log('statusbar', 'overlay failed', { error: String(err) }))
}

// The WebView ships its own asset cache and our Workbox SW conflicts with
// capacitor://localhost asset loading. Firestore's persistentLocalCache keeps
// the offline data layer intact on native.
if (!Capacitor.isNativePlatform()) {
  installStaleChunkReload()
  registerSW({
    immediate: true,
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return

      // Safari rejects update() with "newestWorker is null" while a worker is
      // being replaced; the next check picks the update up, so drop it.
      const checkForUpdate = () => registration.update().catch(() => {})

      // Check for a new SW every hour while the app is open
      setInterval(checkForUpdate, 60 * 60 * 1000)

      // Check immediately when the user switches back to the app
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') checkForUpdate()
      })
    },
  })
}

function renderApp() {
  createRoot(document.getElementById('root')!).render(
    <StrictMode>
      <LocaleContext>
        <Sentry.ErrorBoundary
          fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}
        >
          <AuthProvider>
            <BlockedUsersProvider>
              <FavoriteFriendsProvider>
                <ClockProvider>
                  <TimeFormatProvider>
                    <App />
                  </TimeFormatProvider>
                </ClockProvider>
                <FeedbackDialog />
                <Toaster richColors closeButton />
              </FavoriteFriendsProvider>
            </BlockedUsersProvider>
          </AuthProvider>
        </Sentry.ErrorBoundary>
      </LocaleContext>
    </StrictMode>,
  )
}

// A bundle downloaded last session that never got its background swap goes in
// before the first render (see applyReadyBundleAtLaunch). The checks are local,
// so this costs a few ms; the cap only guards against a hung native call.
const LAUNCH_SWAP_CHECK_MS = 800

if (Capacitor.isNativePlatform()) {
  let rendered = false
  const renderOnce = () => {
    if (rendered) return
    rendered = true
    renderApp()
  }
  void Promise.race([
    applyReadyBundleAtLaunch(renderOnce),
    new Promise<boolean>(resolve => setTimeout(() => resolve(false), LAUNCH_SWAP_CHECK_MS)),
  ]).then(swapping => {
    if (!swapping) renderOnce()
  })
} else {
  renderApp()
}
